/* Copyright (c) Trimble Inc. All rights reserved. */

import { PKCEGenerationHelper } from './PKCEGenerator.helper';
import { getQueryParam } from '../utils/urlBuilder.util';
import { AuthConfigStore, Params } from '../stores/AuthConfig.store';
import { TokenExchangerService } from '../services/TokenExchanger.service';
import { TokenExchangeRequest, TokenExchangeResponse } from '../models/Token.model';
import { getCurrentUrl, Logger } from '@tcp-base/toolkit';
import { OAUTH_CALLBACK_PARAMS } from '../config/CONSTANTS';

/**
 * Token Exchange Service
 *
 * Handles OAuth2 authorization code to access token exchange using PKCE.
 * Manages the complete token exchange flow including PKCE verification.
 */

class TokenExchangeHelperImpl {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}

  /**
   * Validate OAuth callback prerequisites
   *
   * Pure function that checks all required conditions for token exchange
   */
  public validateOAuthCallbackPrerequisites(): {
    isValid: boolean;
    error?: string;
    data?: unknown;
  } {
    // Check if we have the authorization code
    const authorizationCode = getQueryParam('code');
    if (!authorizationCode) {
      return {
        isValid: false,
        error: 'No authorization code found in URL',
      };
    }

    // Get current auth configuration
    const authConfig = AuthConfigStore.getAuthConfig();
    const params = AuthConfigStore.getParams();

    if (!authConfig || !params.clientId || !params.environment) {
      return {
        isValid: false,
        error: 'Authentication not configured. Please ensure AuthProvider is initialized.',
      };
    }

    return {
      isValid: true,
      data: {
        authorizationCode,
        authConfig,
        params,
      },
    };
  }

  /**
   * Process OAuth callback and exchange code for tokens
   *
   * Pure function that:
   * 1. Validates all prerequisites (auth config, clientId, environment)
   * 2. Extracts code from URL
   * 3. Gets PKCE verifier from chain
   * 4. Calls token endpoint
   * 5. Returns result (does NOT store tokens - that's handled by caller)
   */
  public processOAuthCallback = async (): Promise<TokenExchangeResponse> => {
    try {
      // Validate all prerequisites first
      const validation = this.validateOAuthCallbackPrerequisites();
      if (!validation.isValid) {
        return {
          success: false,
          error: validation.error,
        };
      }

      const { authorizationCode, params } = validation.data as {
        authorizationCode: string;
        params: Params;
      };

      // NOTE: URL is intentionally NOT cleaned here.
      // The controller cleans it only after the token is successfully stored so
      // that AuthChecker's isOAuthCallback() check keeps returning true throughout
      // the entire async exchange, preventing a premature login redirect.

      // Get PKCE verifier from chain for token exchange
      const pkceResult = await PKCEGenerationHelper.generate();

      if (!pkceResult.previousCodeVerifier) {
        return {
          success: false,
          error: 'No PKCE code verifier found. PKCE chain may not be initialized.',
        };
      }

      // Prepare token exchange request
      // Note: clientId and environment are validated in validateOAuthCallbackPrerequisites
      // Type assertions are safe here since validation already confirmed these values exist
      if (!params.clientId || !params.environment) {
        return {
          success: false,
          error: 'Missing required parameters after validation',
        };
      }

      const exchangeRequest: TokenExchangeRequest = {
        code: authorizationCode,
        clientId: params.clientId,
        redirectUri: getCurrentUrl(), // Use current URL as redirect URI
        codeVerifier: pkceResult.previousCodeVerifier,
        environment: params.environment,
      };

      // Exchange code for tokens (pure API call)
      const tokens = await TokenExchangerService.ExchangeCodeForTokens(exchangeRequest);

      return {
        success: true,
        tokens,
        redirectUrl: window.location.pathname, // Clean URL without query params
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error during token exchange';
      Logger.error('TokenExchange: Process failed:', error);

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  /**
   * Check if OAuth callback should be processed
   *
   * Pure function that validates all conditions are met
   */
  public shouldProcessOAuthCallback = (): boolean => {
    // Must be OAuth callback with code
    if (!this.isValidOAuthCallback()) {
      return false;
    }

    // Must have auth configuration
    const validation = this.validateOAuthCallbackPrerequisites();
    return validation.isValid;
  };

  /**
   * Check if current URL is an OAuth callback with authorization code
   */
  public isValidOAuthCallback = (): boolean => {
    const code = getQueryParam('code');
    const error = getQueryParam('error');

    // Valid if we have a code and no error
    return !!code && !error;
  };

  /**
   * Get OAuth callback error if present
   */
  public getOAuthCallbackError = (): string | null => {
    const error = getQueryParam('error');
    const errorDescription = getQueryParam('error_description');

    if (error) {
      return errorDescription ? `${error}: ${errorDescription}` : error;
    }

    return null;
  };

  /**
   * Clean OAuth parameters from URL
   * Useful after successful token exchange to clean up the URL
   */
  public cleanOAuthCallbackUrl = (): void => {
    try {
      const url = new URL(window.location.href);

      // Remove OAuth-related parameters
      OAUTH_CALLBACK_PARAMS.forEach((p) => url.searchParams.delete(p));

      // Update URL without refresh
      window.history.replaceState({}, document.title, url.toString());
    } catch (error) {
      Logger.warn('Failed to clean OAuth callback URL:', error);
    }
  };
}

export const TokenExchangeHelper = new TokenExchangeHelperImpl();
export default TokenExchangeHelper;
